"use client"

import { useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { ArtificialTurfCostForm } from "@/components/tools/artificial-turf-cost-form"
import { ArtificialTurfCostResults } from "@/components/tools/artificial-turf-cost-results"
import {
  formatTurfTotalCompactRange,
  formatTurfTotalRange,
  type ArtificialTurfCostResult,
} from "@/lib/artificial-turf-cost-calculator"
import { TURF_GRADE_LABELS } from "@/data/artificial-turf-costs"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard, ResultSuccessCard, ToolPageLoadingFallback, ToolPageShell } from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { Ruler, Hammer, Briefcase, MapPin } from "lucide-react"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"

import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useFromTool } from "@/hooks/use-from-tool"
import { useDefaultLawn } from "@/hooks/use-default-lawn"

const perSqFtRange = (r: { low: number; high: number }) =>
  r.low === r.high ? `$${r.low.toFixed(2)}` : `$${r.low.toFixed(2)} to $${r.high.toFixed(2)}`

function ArtificialTurfCostContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("artificial-turf-cost-calculator")
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { subscription } = useAuth()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()
  const [result, setResult] = useState<ArtificialTurfCostResult | null>(null)
  const trackedUsageRef = useRef(false)

  // The form computes the estimate live (once the ZIP resolves to a known
  // region) and streams it up. Usage analytics fire exactly once per session
  // via the ref guard.
  const handleResultChange = (
    calculationResult: ArtificialTurfCostResult | null,
    inputs?: { removeExistingLawn: boolean },
  ) => {
    setResult(calculationResult)
    if (calculationResult && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage("artificial-turf-cost-calculator")
      analytics.calculatorUsed("artificial-turf-cost", {
        inputs: {
          zip_code: calculationResult.zipCode,
          area_sqft: calculationResult.area,
          turf_grade: calculationResult.turfGrade,
          install_method: calculationResult.installMethod,
          remove_existing_lawn: !!inputs?.removeExistingLawn,
        },
        result: {
          region: calculationResult.region,
          total_low: calculationResult.totalCost.low,
          total_high: calculationResult.totalCost.high,
          cost_per_sqft_low: calculationResult.costPerSqFt.low,
          cost_per_sqft_high: calculationResult.costPerSqFt.high,
          diy_total_high: calculationResult.diyTotal.high,
          pro_total_high: calculationResult.proTotal.high,
        },
        userTier: subscription?.isPremium ? "premium" : "free",
        location: calculationResult.zipCode,
        source: "live_update",
      })
    }
  }

  return (
    <ToolPageShell
      breadcrumbLabel="Artificial Turf Cost Calculator"
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Artificial Turf Cost Calculator"
          usageCount={usageCount}
          zipCode={result?.zipCode}
        />
      }
    >
      <HeadlineHero
        eyebrow="Personalized to your ZIP"
        eyebrowIcon={<MapPin className="h-3.5 w-3.5" aria-hidden="true" />}
        title="What does artificial turf"
        accent="actually cost to install?"
        subtitle="Free artificial grass installation cost calculator with DIY and professional estimate ranges by area, turf grade, and region."
      />

      <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
      <ArtificialTurfCostForm
        initialValues={{
          zipCode: searchParams.get("zip") ?? lawnPrefill.zipCode,
          area: searchParams.get("size") ? undefined : lawnPrefill.lawnSizeSqft,
        }}
        onResultChange={handleResultChange}
      />

      {result && (
        <div id="results" className="mt-8 space-y-6">
          <TurfSuccessCard result={result} />
          <ResultEmailCaptureCard
            source="tool_result_artificial_turf"
            toolDisplayName="turf cost estimate"
            subcopy="Occasional yard project cost tips. No spam."
            accountLead={{
              redirectTo: buildToolUrl("artificial-turf-cost-calculator", {
                zip: result.zipCode,
                lawnSize: result.area,
                turfGrade: result.turfGrade,
                installMethod: result.installMethod,
                removeExistingLawn: result.removeExistingLawn,
              }),
              headline: "Save my turf cost estimate",
              subcopy: "Keep this estimate in a free account and revisit it anytime.",
              dividerLabel: "or get it by email",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_artificial_turf"),
            }}
            successSubcopy="We'll send the occasional yard project cost tip. Your turf estimate stays right here to revisit anytime."
            onCaptured={() => {
              closeNewsletterModal()
            }}
          />
          <ArtificialTurfCostResults result={result} />
        </div>
      )}

      <RelatedTools
        className="mt-10"
        currentToolId="artificial-turf-cost-calculator"
        prefillValues={{
          zip: searchParams.get("zip") ?? lawnPrefill.zipCode,
          lawnSize: result?.area ?? lawnPrefill.lawnSizeSqft,
        }}
      />
    </ToolPageShell>
  )
}

/**
 * Bounded peak-moment frame for the turf cost result. Hero = the total cost
 * RANGE (compact k-notation so it never overflows the hero on mobile; the
 * $100 grain of formatTurfTotal keeps it equal to the full-form figures). The
 * interpretation carries the precise range + the "these are estimates, get
 * quotes" honesty. Grass-less tool, so the copy names no grass and the email
 * card promises no calendar. Next action: compare the cost of real sod.
 */
function TurfSuccessCard({ result }: { result: ArtificialTurfCostResult }) {
  const methodLabel = result.installMethod === "diy" ? "DIY" : "professional"
  const areaLabel = result.area.toLocaleString()

  const sodHref = buildRelatedToolUrl("sod-calculator", "artificial-turf-cost-calculator", {
    zip: result.zipCode,
    lawnSize: result.area,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "artificial-turf-cost-calculator",
      targetTool: "sod-calculator",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Compare the cost of real sod against artificial turf",
      prefillFields: ["zip", "size"].filter((f) => (f === "zip" ? Boolean(result.zipCode) : true)).join(","),
    })
  }

  const stats: ResultSuccessCardStat[] = [
    { icon: Ruler, label: "Per sq ft", value: perSqFtRange(result.costPerSqFt) },
    { icon: Hammer, label: "DIY est.", value: formatTurfTotalCompactRange(result.diyTotal) },
    { icon: Briefcase, label: "Pro est.", value: formatTurfTotalCompactRange(result.proTotal) },
  ]

  return (
    <ResultSuccessCard
      heroValue={formatTurfTotalCompactRange(result.totalCost)}
      heroLabel={`estimated ${methodLabel} install cost`}
      stats={stats}
      interpretation={
        <>
          Installing {areaLabel} sq ft of {TURF_GRADE_LABELS[result.turfGrade].toLowerCase()} grade artificial turf in the {result.region} region runs about {formatTurfTotalRange(result.totalCost)} ({perSqFtRange(result.costPerSqFt)} per sq ft) as a {methodLabel} project. These are estimate ranges, not quotes, so get a few local quotes before you commit.
        </>
      }
      cta={{
        href: sodHref,
        label: "Compare the cost of real sod",
        onClick: handleCtaClick,
      }}
      share={{
        url: buildToolUrl("artificial-turf-cost-calculator", {
          zip: result.zipCode,
          lawnSize: result.area,
          turfGrade: result.turfGrade,
          installMethod: result.installMethod,
          removeExistingLawn: result.removeExistingLawn,
        }),
        title: "Artificial Turf Cost Estimate",
        text: `Estimated cost to install ${areaLabel} sq ft of artificial turf`,
        trackingId: "artificial-turf-cost-calculator",
      }}
    />
  )
}

// Default export with Suspense boundary for useSearchParams
export function ArtificialTurfCostPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="artificial-turf-cost-calculator" />}>
      <SearchParamsHydrationGate>
        <ArtificialTurfCostContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
